import Cart from '@/models/cart/Cart';
import CartItemGoods from '@/models/cart/CartItemGoods';
import CartItemRank from '@/models/cart/CartItemRank';
import GoodsOption from '@/models/cart/GoodsOption';
import CartRepository from '@/repository/cart/CartRepository';
import * as StringUtil from '@/common/StringUtil';
import PutCartItem from '@/models/cart/apiParam/PutCartItem';
import CartPurchaseParam from '@/models/cart/apiParam/CartPurchaseParam';
import CalcCart from '@/models/cart/CalcCart';

const format = 'YYYY年MM月DD日（dd） HH:mm:ss';

const decodeCartItemGoods = (item: any) => {
  const cartItemGoods = new CartItemGoods();
  cartItemGoods.cartItemId = Number(item.cart_item_id);
  cartItemGoods.code = item.item_code;
  cartItemGoods.title = item.item_title;
  cartItemGoods.quantity = Number(item.quantity);
  cartItemGoods.examFeeType = item.exam_fee_type;
  cartItemGoods.canBuy = item.can_buy;
  cartItemGoods.registrationRequired = item.registration_required;
  cartItemGoods.resourceUri = item.resource_uri;
  cartItemGoods.alternativeText = item.alternative_text;
  item.fees.forEach((v: any) => {
    cartItemGoods.fees.push(Number(v.fee));
  });
  cartItemGoods.totalFee = Number(item.total_fee);
  cartItemGoods.periodStart = StringUtil.toJapanDateString(item.period_start, format);
  cartItemGoods.periodEnd = StringUtil.toJapanDateString(item.period_end, format);

  if (item.cart_item_options !== undefined && Array.isArray(item.cart_item_options)) {
    item.cart_item_options.forEach((v: any) => {
      const goodsOption = new GoodsOption();
      goodsOption.goodsOptionId = v.goods_option_id;
      goodsOption.goodsOptionTypeTitle = v.goods_option_type_title;
      goodsOption.optionTitle = v.option_title;
      cartItemGoods.goodsItemOptions.push(goodsOption);
    });
  }
  return cartItemGoods;
};

const decodeCartItemRank = (item: any) => {
  const cartItemRank = new CartItemRank();
  cartItemRank.cartItemId = Number(item.cart_item_id);
  cartItemRank.code = item.item_code;
  cartItemRank.title = item.item_title;
  cartItemRank.quantity = Number(item.quantity);
  cartItemRank.examFeeType = item.exam_fee_type;
  cartItemRank.canBuy = item.can_buy;
  cartItemRank.registrationRequired = item.registration_required;
  cartItemRank.resourceUri = item.resource_uri;
  cartItemRank.alternativeText = item.alternative_text;
  item.fees.forEach((v: any) => {
    cartItemRank.fees.push(Number(v.fee));
  });
  cartItemRank.totalFee = Number(item.total_fee);
  cartItemRank.periodStart = StringUtil.toJapanDateString(item.period_start, format);
  cartItemRank.periodEnd = StringUtil.toJapanDateString(item.period_end, format);

  return cartItemRank;
};

const decodeCartItem = (item: any) => {
  if (!StringUtil.isEmpty(item.goods_code)) {
    return decodeCartItemGoods(item);
  }
  if (!StringUtil.isEmpty(item.exam_rank_code)) {
    return decodeCartItemRank(item);
  }
  throw new Error();
};

export const decodeCart = (res: any) => {
  const cart = new Cart();
  cart.cartCode = res.data.cart_code;
  cart.totalFee = res.data.total_fee;
  cart.discount = res.data.discount;

  res.data.cart_items.forEach((v: any) => {
    const cartItem = decodeCartItem(v);
    cart.cartItems.push(cartItem);
  });
  return cart;
};

export const SESSION_CART_CODE_KEY = 'cart-code';

export const getNewCart = async (cartRepo: CartRepository) => {
  let cart: Cart | undefined;
  await cartRepo.getNewCart().then((res: any) => {
    cart = new Cart();
    cart.cartCode = res.data.cart_code;
  });
  return cart;
};

export const getCart = async (cartRepo: CartRepository, cartCode: string) => {
  let cart: Cart | undefined;
  if (StringUtil.isEmpty(cartCode)) {
    return undefined;
  }
  await cartRepo.getCart(cartCode).then((res: any) => {
    if (res.status !== 200) {
      return undefined;
    }
    cart = decodeCart(res);
    return cart;
  });
  return cart;
};

export const addCartItem = async (cartRepo: CartRepository, cartItem: PutCartItem) => {
  const status = await cartRepo.putCartItem(cartItem).then((res: any) => res.status);
  return Number(status);
};

export const removeCartItem = async (cartRepo: CartRepository, cartCode: string, cartItemId: number) => {
  await cartRepo.removeCartItem(cartCode, cartItemId).then((res) => res);
};

export const changeQuantity = async (cartRepo: CartRepository, cartCode: string, cartItemId: number, newQuantity: number) => {
  await cartRepo.changeQuantity(cartCode, cartItemId, newQuantity);
};

export const calcCart = async (cartRepo: CartRepository, param: CartPurchaseParam) => {
  const calcResultCart = await cartRepo.calcCart(param).then((response) => {
    let result: CalcCart | undefined;
    if (response.status !== 201) {
      return result;
    }
    try {
      result = cartRepo.convert.deserialize(response.data, CalcCart) as CalcCart;
    } catch {
      return result;
    }
    return result;
  });
  return calcResultCart;
};

export const purchaseCart = async (cartRepo: CartRepository, param: CartPurchaseParam) => {
  let response: any;
  await cartRepo.purchaseCart(param).then((res) => { response = res; });
  return response;
};

export default getCart;
